import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx/xlsx.mjs';
import {CommonUseFields, LATEST_STABLE_LEVELS} from "@app/constants/Common";
import _, {cloneDeep} from 'lodash'
import {autoCorrectStrPhone, isYear} from "@app/utils/ValidationUtils";
import {standardEmails} from "@app/utils/EmailUtils";
import {standardPrefixPhones} from "@app/utils/PhoneUtils";
import {applyTextCase, parseMergeToken, resolveMergeFieldKey} from "@app/utils/MailMergeFields";

export const capitalizeName = (str, lower = false) => {
    if (!str) {
        return ""
    }
    return (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase());
}

export const capitalizeArray = (arr, lower = false) => {
    if (!arr) {
        return ""
    }

    return  arr.map(item => {
        return capitalizeName(item)
    })
}

export const strTrim = (str) => {
    if (str) {
        return str.toString().trim()
    }
    return ""
}

export const strEqual = (str1, str2) => {
    if (!str1 && !str2) {
        return true;
    }
    if (!str1 || !str2) {
        return false;
    }
    return str1.toLowerCase() === str2.toLowerCase();
}

// option (value, label)
export const optionsToString = (options, isMulti=true) => {
    if (!options || options.length === 0) {
        return ""
    }

    if (typeof options === 'string' || options instanceof String) {
        return options
    }

    // Trường hợp option chỉ được phép chọn 1 giá trị
    if (!isMulti) {
        return options.value
    }

    if (Array.isArray(options)) {
        let arrays = [];
        options.forEach(p => {
            arrays.push(p.value);
        });
        return arrays.join(';')
    }
    return options

}

// option (value, label)
export const optionsToArray = (options) => {
    if (options && options.length > 0 && Array.isArray(options)) {
        let arrays = [];
        options.forEach(p => {
            arrays.push(p.value);
        });
        return arrays
    }
    return []

}

export const stringToArray = (str, char=";") => {
    if (!str) {
        return []
    }

    if (Array.isArray(str)) {
        return str
    }

    // Todo: fix some bugs using old char ","
    str = str.toString()
    str = str.replaceAll(",", char)

    return str.split(char)
}

// input may be string, number, array, options
export const toArray = (input, char=";") => {
    if (!input || input.length === 0) {
        return []
    }

    // Trường hợp input là array nhưng không phải là option (value, label)
    if (Array.isArray(input) && !input[0].value) {
        return input
    }

    // Trường hợp input là array và là option (value, label)
    if (Array.isArray(input) && input[0].value) {
        return input.map(item => {
            return item.value
        })
    }

    return input.toString().split(char)
}

export const stringToOptionWithChar = (str, char=";", isMulti=true, isCapitalize=true) => {
    if (!str) {
        return isMulti ? [] : {}
    }

    if (typeof str !== 'string' && !(str instanceof String)) {
        return str
    }

    if (Array.isArray(str)) {
        return str
    }

    if (!isMulti) {
        return {
            value: str,
            label: isCapitalize ? capitalizeName(str) : str
        }
    }

    const arr = str.split(char)
    return arr.map(item => {
        return {
            value: item,
            label: isCapitalize ? capitalizeName(item) : str
        }
    })
}

export const stringToOption = (str, isMulti=true, isCapitalize=true) => {
    return stringToOptionWithChar(str, ";", isMulti, isCapitalize)
}

export const stringToSingleOption = (str) => {
    return stringToOption(str, ";", false)
}

export const arrayToString = (array, char=";") => {
    if (!array) {
        return ""
    }
    if (Array.isArray(array)) {
        return array.join(char)
    }
    return array
}

export const monthOptions = () => {
    let months = [{
        value: '',
        label: ''
    }];
    for (let y = 1; y <= 12; y++) {
        months.push({
            value: y,
            label: y
        });
    }
    return months
}


export const yearOptions = () => {
    let years = [{
        value: '',
        label: ''
    }];
    let currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= 1900; y--) {
        years.push({
            value: y,
            label: y
        });
    }
    return years
}


export const createRowDataExcel = (item) => {
    item.display_certificates = ""
    if (item.certificates && item.certificates.length > 0) {
        item.certificates.forEach((certificate, index) => {
            if (index === item.certificates.length - 1) {
                item.display_certificates += certificate.special_certificate
            } else {
                item.display_certificates += (item.display_certificates + certificate.special_certificate + ",")
            }
        })
    }
    return {
        'COMPANY_NOTE': item.career.company_note,
        'COMPANY_PROVINCE_NOTE': item.career.company_province_note,
        'COMPANY_INDUSTRY_NOTE': item.career.company_industry_note,
        'INDUSTRY': item.industry,
        'INDUSTRY_SEGMENT1': item.industry_segment1,
        'INDUSTRY_SEGMENT2': item.industry_segment2,
        'FUNCTION': item.function,
        'FUNCTION_SEGMENT1': item.function_segment1,
        'FUNCTION_SEGMENT2': item.function_segment2,
        'SPECIAL_CERTIFICATE': item.display_certificates,
        'HIGHEST_LEVEL': item.highest_level,
        'LOCATION_PROVINCE': optionsToString(item.local_province),
        'LOCATION_PROVINCE_EMAIL': optionsToString(item.local_province_email),
        'LOCATION_NOTE': item.location_note,
        'LOCAL_NATIONALITY': item.local_nationality,
        'CALLER_NAME_TO_EMAIL': item.caller_name_to_email,
        'ORIGIN_COMPANY': item.career.origin_company,
        'TITLE': item.career.title,
        'COMPANY_EMAIL': item.career.company_email,
        'FULL_NAME': item.full_name,
        'NAME_EMAIL': item.email_name,
        'VOCATIVE': item.vocative,
        'WORK_START_TIME': item.career.work_start_month ?
            item.career.work_start_month + "/" + item.career.work_start_year : item.career.work_start_year,
        'WORK_TIME': item.career.work_month ?
            item.career.work_month + "/" + item.career.work_year : item.career.work_year,
        'WORK_END_TIME': item.career.work_end_month ?
            item.career.work_end_month + "/" + item.career.work_end_year : item.career.work_end_year,
        'ENGAGEMENT_STATUS': item.engagement_status,
        'GENERAL_NOTE': item.general_note,
        'ORIGIN': item.origin.origin,
        'ORIGIN_NOTE': item.origin.origin_note,
        'PHONE': item.phone,
        'EMAIL': item.email,
        'EMAIL_TO_SEND': item.email_to_send,
        'VNW': item.vnw,
        'LINKEDIN': item.linkedin,
        'SKYPE': item.skype,
        'APPEARENCE': item.appearence,
        'EVER_EMAIL': item.ever_email,
        'BLOCK_EMAIL': item.block_email,
        'CANDIDATE_KNOW_STATE': item.flow_categories.candidate_know_state,
        'PERSONAL_NOTE': item.personal_note,
        'BLOCK_STATE': item.flow_categories.block_state,
        'CALL_UV_1': ((item.caller_name_to_email || '').toLowerCase()) + ' ' + capitalizeName(item.email_name),
        'CALL_UV_2': capitalizeName(item.caller_name_to_email) + ' ' + capitalizeName(item.email_name),
        'CALL_UV_3': ((item.caller_name_to_email || '').toUpperCase()) + ' ' + ((item.email_name || '').toUpperCase()),
        // TODO:
        // 'XUNG_HO_EMAIL': caller_name_to_email === 'anh' || caller_name_to_email === 'chị' ? DEFAULT_ADMIN_NAME : item.vocative,
        // 'XUNG_HO_EMAIL': item.vocative,
        // 'LOCATION_AREA': optionsToString(item.local_area),
        // 'LOCATION_AREA_EMAIL': optionsToString(item.local_area_email),
        // 'GENDER': item.gender,
        // 'DISPLAYED_COMPANY': item.displayedCareer.origin_company,
        // 'DISPLAYED_TITLE': item.displayedCareer.title,
        // 'NAME_VN': item.vn_name,
        // 'NAME_FOREIGN': item.foreign_name,
        // 'LATEST_STABLE_LEVEL': LATEST_STABLE_LEVELS[item.latest_stable_level],
    };
}

export const exportCandidatesExcel = (candidates) => {
    const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let exportData = [];
    candidates.forEach(item => {
        item.displayedCareer = {
            origin_company: "",
            title: ""
        }

        item.career = {}
        if (item.career_summaries && item.career_summaries.length > 0) {
            item.displayedCareer = {...item.career_summaries[0]}
            item.career_summaries.forEach(career => {
                if (career.displayed_flag) {
                    item.displayedCareer = career
                }
                item.career = career
                exportData.push(createRowDataExcel(item))
            })
        } else {
            exportData.push(createRowDataExcel(item))
        }
    });
    const ws = XLSX.utils.book_new();
    XLSX.utils.sheet_add_aoa(ws, [['NOTE Company', 'Company Loca (tỉnh)', 'Company Industry', 'Industry', 'Industry - phân khúc 1',
        'Industry - phân khúc 2', 'Function', 'Function - phân khúc 1', 'Function - phân khúc 2', 'Chứng chỉ đặc biệt', 'Highest Level',
        'Location (Tỉnh)', 'Location Tỉnh (to Email)', 'Note Location', 'Quốc tịch',
        'Gọi UV là (email)', 'Company (ttin goc)', 'Title', 'Email công ty',
        'Họ & tên', 'Tên (to Email)', 'User xưng là (email)',
        'Time bắt đầu', 'Time có làm', 'Time kết thúc', 'Engagement Status', 'General Note',
        'Nguồn UV', 'Notes (Nguồn UV)',
        'SĐT di dộng', 'Email cá nhân', 'Email cá nhân (to send)', 'Link profile VNW', 'Link profile Linkedin', 'Nick Skype',
        'Ngoai hinh', 'Email TỪNG dùng/lỗi', 'Email blocked', 'UV lạ/quen', 'Note thông tin cá nhân', 'Block Status', 'Gọi UV 1', 'Gọi UV 2', 'Gọi UV 3'
        ]]);
    XLSX.utils.sheet_add_json(ws, exportData, {origin: 'A2', skipHeader: true});
    const wb = {Sheets: {data: ws}, SheetNames: ['data']};
    const excelBuffer = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
    const data = new Blob([excelBuffer], {type: fileType});
    FileSaver.saveAs(data, `Candidate_export_${new Date().getTime()}.xlsx`);
}


export const doExportExcel = (canList) => {
    let exportCandidates = []

    canList.forEach(can => {
        exportCandidates.push({
            ...can.general,
            ...can.personal,
            ...can.location,
            ...can.contact,
            ...{origin:can.origin},
            ...can.candidateNotes,
            ...can.categories,
            ...{career_summaries: can.career_summaries},
            ...{certificates: can.certificates},
            ...{flow_categories: can.flow_categories},
        })
    })
    exportCandidatesExcel(exportCandidates)
}

export const compareString = (str1, str2) => {
    console.log("compareString")
    if (!str1 && !str2) {
        return true
    }
    if (!str1 || !str2) {
        return false
    }

    console.log("str1=" + str1 + "; str2="+str2)
    return strTrim(str1).toLowerCase() === strTrim(str2).toLowerCase()
}

export const stringToLowerCase = (str) => {
    if (!str) {
        return "";
    }
    return str.toLowerCase();
}

export const arrayToLowerCase = (arr) => {
    if (!arr) {
        return []
    }

    return arr.map(item => {
        if (typeof item === 'string' || item instanceof String) {
            return item.toString().toLowerCase()
        }
        if (item.value) {
            item.value = item.value.toString().toLowerCase()
        }
        return item
    })
}

export const compareArrays = (arr1, arr2) => {
    console.log("arr1:" + JSON.stringify(arr1))
    console.log("compareArrays")
    if (!arr1 && !arr2) {
        return true
    }
    if (!arr1 || !arr2) {
        return false
    }
    if (arr1.length !== arr2.length) {
        return false
    }
    arr1 = arrayToLowerCase(arr1.sort())
    arr2 = arrayToLowerCase(arr2.sort())

    for (let i=0; i < arr1.length; i++) {
        if (arr1[i].trim() !== arr2[i].trim()) {
            return false
        }
    }
    return true
    // return _.isEqual(arr1.sort(), arr2.sort())
}


export const compareOptions = (ops1, ops2) => {
    console.log("compareOptions")
    if (!ops1 && !ops2) {
        return true
    }

    if (!ops1 || !ops2) {
        return false
    }

    const arr1 = ops1.map(op => {
        return op.value
    })

    const arr2 = ops2.map(op => {
        return op.value
    })

    return _.isEqual(arr1, arr2)
}

export const twoArrayHaveSameElement = (arr1, arr2) => {
    if (!arr1 || !arr2) {
        return true
    }
    if (!Array.isArray(arr1) || !Array.isArray(arr1) ) {
        return false
    }
    let hasSame = false
    arr1.forEach(a1 => {
        arr2.forEach(a2 => {
            if (strTrim(a1.toLowerCase()) === strTrim(a2.toLowerCase())) {
                hasSame = true
            }
        })
    })
    return hasSame
}

export const intersectionBetweenTwoArrays = (arr1, arr2) => {
    if (!arr1 || !arr2 || arr1.length === 0 || arr2.length === 0) {
        return []
    }
    let arr1LowerCase = arr1.map(a1 => {
        return a1.toLowerCase()
    })
    let arr2LowerCase = arr2.map(a2 => {
        return a2.toLowerCase()
    })

    let result = []
    arr1LowerCase.forEach(item => {
        if (!arr2LowerCase.includes(item)) {
            result.push(item)
        }
    })

    return result
}

// Phương thức check arr1 nằm trong arr2 hay ko
// Trường toàn bộ phần tử trong arr1 đều thuộc arr2 => true, ngược lại có 1 phần từ của arr1 không thuộc arr2 => return false.
export const checkEmailToSendInOtherEmails = (arrEmailToSend, arrOtherEmails) => {
    // if one param is null or empty => ignore check
    if (!arrEmailToSend || !arrOtherEmails || arrEmailToSend.length === 0 || arrOtherEmails.length === 0) {
        return true
    }

    const inter = intersectionBetweenTwoArrays(arrEmailToSend, arrOtherEmails)
    return inter.length === 0
}

// Phương thức check 2 mảng có phần tử khác nhau
// Trường hợp có phần tử thuộc mảng này, nhưng ko thuộc mảng kia => return true
export const twoArrayHaveNotSameElement = (arr1, arr2) => {
    if (!arr1 || !arr2 || arr1.length === 0 || arr2.length === 0) {
        return false
    }

    if (!Array.isArray(arr1) || !Array.isArray(arr1) ) {
        return false
    }

    let hasNotSame = false
    arr1.forEach(a1 => {
        if (!arr2.includes(a1)) {
            hasNotSame = true
        }
    })
    return hasNotSame

}

export const removeStrSpace = (str) => {
    if (str) {
        return str.toString().replaceAll(" ", "")
    }
    return ""
}

export const appendOptions = (op1, op2) => {
    if ((!op1 && !op2)) {
        return []
    }

    if (!op1) {
        return op2
    }

    if (!op2) {
        return op1
    }

    let result = []
    let tempValue = []
    op1.forEach(o => {
        if (!tempValue.includes(o.value)) {
            tempValue.push(o.value)
            result.push(o)
        }
    })

    op2.forEach(o => {
        if (!tempValue.includes(o.value)) {
            tempValue.push(o.value)
            result.push(o)
        }
    })

    return result
}

export const appendArrayValues = (arr1, arr2) => {
    if ((!arr1 && !arr2)) {
        return []
    }

    if (!arr1) {
        return arr2
    }

    if (!arr2) {
        return arr1
    }

    let result = []
    let tempValue = []
    arr1.forEach(o => {
        if (!tempValue.includes(o)) {
            tempValue.push(o)
            result.push(o)
        }
    })

    arr2.forEach(o => {
        if (!tempValue.includes(o)) {
            tempValue.push(o)
            result.push(o)
        }
    })

    return result
}

export const getGenderByCallerName = (callerName) => {
    if (callerName === 'anh') {
        return 'nam';
    }
    if (callerName === 'chị') {
        return 'nữ';
    }
    return ''
}


export const arrayIncludeItem = (arr=[], str) => {
    if (!arr || arr.length === 0 || !str) {
        return false
    }
    for (let i=0; i < arr.length; i++) {
        if (arr[i].toLowerCase() === str.toLowerCase()) {
            return true
        }
    }

    return false
}

export const eclipSizeText = (str, maxLength = 100) => {
    if (!str || str.length <= maxLength) {
        return str
    }
    return str.substring(0, maxLength) + "..."
}

export const isBlank = (str) => {
    return (!str || str.length === 0)
}

export const readCandidatesFromTemExcelFile = (file, userLoginId, callback) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const dataList = XLSX.utils.sheet_to_json(worksheet);
        if (dataList.length > 0) {
            let candidateList = []
            let headers = []
            for (let index = 0; index < dataList.length; index++) {
                const item = dataList[index]
                if (index === 0) {
                    headers = Object.values(item);
                }
                if (index === 0 && Object.keys(item).length < 35) {
                    alert("File excel không đủ 35 cột.")
                    return;
                }

                // ignore header
                if (index > 0) {
                    // validate year
                    let errors = []
                    if (item[19] && !isYear(item[19])) {
                        errors.push(`Giá trị "time bắt đầu" (${item[19]}) ở dòng ${index + 2} không đúng định dạng năm.`)
                    }
                    if (item[20] && !isYear(item[20])) {
                        errors.push(`Giá trị "time có làm" (${item[20]}) ở dòng ${index + 2} không đúng định dạng năm.`)
                        return;
                    }
                    if (item[21] && !isYear(item[21])) {
                        errors.push(`Giá trị "time kết thúc" (${item[21]}) ở dòng ${index + 2} không đúng định dạng năm.`)
                    }
                    if (errors.length > 0) {
                        alert(errors.join("\n"))
                        return;
                    }

                    // const extractResult = {
                    //     career_summaries: [
                    //         {
                    //             company_note: item[0],
                    //             company_province_note: item[1],
                    //             company_industry_note: item[2],
                    //             origin_company: item[15],
                    //             title: item[16],
                    //             start_time: item[19],
                    //             work_time: item[20],
                    //             end_time: item[21],
                    //         },
                    //     ],
                    //     category: {
                    //         industry: item[3],
                    //         industry_segment1: item[4],
                    //         industry_segment2: item[5],
                    //         function: item[6],
                    //         function_segment1: item[7],
                    //         function_segment2: item[8],
                    //         highest_level: item[10],
                    //     },
                    //     certificate: {
                    //         special_certificate: item[9],
                    //     },
                    //     location: {
                    //         local_province: item[11],
                    //         local_province_email: item[12],
                    //         local_note: item[13],
                    //     },
                    //     personal: {
                    //         caller_name_to_email: item[14],
                    //         full_name: item[17],
                    //         email_name: item[18],
                    //         appearence: item[32],
                    //     },
                    //     contact: {
                    //         phone: item[26],
                    //         email: item[27],
                    //         email_to_send: item[28],
                    //         ever_email: item[33],
                    //         block_email: item[34],
                    //         vnw: item[29],
                    //         linkedin: item[30],
                    //         skype: item[31],
                    //     },
                    //     candidate_note: {
                    //         engagement_status: item[22],
                    //         general_note: item[23],
                    //     },
                    //     origin: {
                    //         origin: item[24],
                    //         origin_note: item[25],
                    //     },
                    // }


                    const extractResult = {
                        career_summaries: [
                            {
                                company_note: strTrim(item[0]),
                                company_province_note: strTrim(item[1]),
                                company_industry_note: strTrim(item[2]),
                                origin_company: strTrim(item[15]),
                                title: strTrim(item[16]),
                                work_start_year: {value: strTrim(item[19]), label: strTrim(item[19])},
                                work_start_month: {value: '', label: ''},
                                work_year: {value: strTrim(item[20]), label: strTrim(item[20])},
                                work_month: {value: '', label: ''},
                                work_end_year:{value: strTrim(item[21]), label: strTrim(item[21])},
                                work_end_month:{value: '', label: ''},
                                created_user: userLoginId,
                                from_excel: true
                            },
                        ],
                        industry: item[3] ? [strTrim(item[3])] : [],
                        industry_segment1: item[4] ? [strTrim(item[4])] : [],
                        industry_segment2: item[5] ? [strTrim(item[5])] : [],
                        function: item[6] ? [strTrim(item[6])] : [],
                        function_segment1: item[7] ? [strTrim(item[7])] : [],
                        function_segment2: item[8] ? [strTrim(item[8])] : [],
                        special_certificate: strTrim(item[9]),
                        highest_level: strTrim(item[10]),
                        local_province: strTrim(item[11]),
                        local_province_email: strTrim(item[12]),
                        local_note: strTrim(item[13]),
                        caller_name_to_email: strTrim(item[14]),
                        full_name: strTrim(item[17]),
                        email_name: strTrim(item[18]),
                        engagement_status: strTrim(item[22]),
                        general_note: strTrim(item[23]),
                        origin: strTrim(item[24]).toLowerCase(),
                        origin_note: strTrim(item[25]),
                        phone: item[26] ? [removeStrSpace(item[26])] : [],
                        email: item[27] ? [removeStrSpace(item[27])] : [],

                        // email to send auto fill value of email.
                        email_to_send: item[27] ? [removeStrSpace(item[27])] : [],

                        vnw: item[29] ? [removeStrSpace(item[29])] : [],
                        linkedin: item[30] ? [removeStrSpace(item[30])] : [],
                        skype: item[31] ? [removeStrSpace(item[31])] : [],
                        appearence: strTrim(item[32]),
                        ever_email: item[33] ? [removeStrSpace(item[33])] : [],
                        block_email: item[34] ? [removeStrSpace(item[34])] : [],
                    }
                    extractResult.phone = [...stringToArray(extractResult.phone)].join(";")
                    let candidate = cloneDeep(extractResult)

                    candidate.require_email = !isBlank(candidate.email)
                    candidate.require_ever_email = !isBlank(candidate.ever_email)
                    candidate.require_block_email = !isBlank(candidate.block_email)
                    candidate.email = standardEmails(candidate.email)
                    candidate.email_to_send = standardEmails(candidate.email_to_send)
                    candidate.ever_email = standardEmails(candidate.ever_email)
                    candidate.block_email = standardEmails(candidate.block_email)

                    candidate.require_phone = !isBlank(candidate.phone)
                    candidate.phone = autoCorrectStrPhone(candidate.phone)
                    candidate.phone = standardPrefixPhones(candidate.phone)
                    candidate.extract_result = extractResult
                    candidate.db_candidate_info = {}
                    candidate.standard_info = {}
                    candidateList.push(candidate)
                }
            }

            callback(candidateList, headers)
        } else {
            alert("Không có thông tin person trong file excel.")
            callback([])
        }
    };
    reader.readAsArrayBuffer(file);
}

/**
 * Fill giá trị vào các placeholder {{...}} của mail template.
 *
 * Placeholder được lưu bằng nhãn tiếng Việt ({{Tên ứng viên}}) và có thể kèm modifier
 * hoa/thường ({{Tên ứng viên|upper}}). Template cũ lưu bằng tên column ({{full_name}})
 * vẫn merge được vì resolveMergeFieldKey trả lại nguyên chuỗi khi không khớp nhãn nào.
 */
export const mergeTemplateBody = (template, vars) => {
  if (!template) return '';
  return template.replace(/\{\{([^}]+)\}\}/g, (_, token) => {
    const {name, textCase} = parseMergeToken(token);
    return applyTextCase(vars[resolveMergeFieldKey(name)] ?? '', textCase);
  });
};
